import { coverage, loadScoreBase, readingEffort, rereadRatio, type SectionStat } from "./engagementMetrics"

/**
 * Pure reading-behaviour state machine for one answer view. Time is always passed in (ms), so it is
 * unit-testable; the DOM wiring lives in useAnswerEngagement. It holds timings, counts and indexes only:
 * the caller counts words from the text locally and never hands the text over.
 */

export type ExitReason = "followup" | "navigate" | "close" | "idle"
export type AbortPhase = "streaming" | "reading"

export const IDLE_MS = 120_000
export const REVERSAL_PX = 120
export const HOVER_MS = 300
export const READING_ABORT_MS = 5000
export const READING_ABORT_DEPTH = 30
export const ENGAGED_COVERAGE = 0.5
export const ENGAGED_DWELL_MS = 10_000
const MAX_SECTIONS = 200

/** The POST body of /api/v1/telemetry/answer-engagement (contract section 1). */
export interface EngagementPayload {
  app: "nira"
  view_id: string
  session_id: string
  turn_key: string
  request_id: string | null
  mode: string | null
  answer_words: number
  section_count: number
  sources_count: number
  streamed: boolean
  stream_ms: number | null
  first_interaction_ms: number | null
  dwell_ms: number
  hidden_ms: number
  tab_switches: number
  max_depth_pct: number
  scroll_reversals: number
  selections: number
  copies: number
  citation_hovers: number
  citation_clicks: number
  sections: { i: number; words: number; visible_ms: number; visits: number }[]
  aborted: boolean
  abort_phase: AbortPhase | null
  abort_section: number | null
  exit: ExitReason | null
  final: boolean
}

export interface ViewInit {
  viewId: string
  sessionId: string
  turnKey: string
  /** The page opened an answer that was already finished (history), rather than watching it stream. */
  streamed: boolean
  /** When the view began (ms). For a finished answer this is also when it was "complete". */
  now: number
  requestId?: string | null
  mode?: string | null
  /** Page visible and focused at the start. */
  active?: boolean
  /** The user can see the page's tab (for hidden_ms). */
  visible?: boolean
}

const int = (n: number) => Math.max(0, Math.round(n))

export class EngagementView {
  readonly viewId: string
  readonly sessionId: string
  readonly turnKey: string
  requestId: string | null
  mode: string | null
  sourcesCount = 0

  private streamed: boolean
  private streamStart: number | null
  private streamMs: number | null = null
  private completedAt: number | null
  private words: number[] = []
  private ms: number[] = []
  private visits: number[] = []
  private onScreen = new Set<number>()

  private last: number
  private active: boolean
  private visible: boolean
  private dwell = 0
  private hidden = 0
  private tabSwitches = 0

  private depth = 0
  private reversals = 0
  private peak: number | null = null
  private trough: number | null = null
  private rising = false

  private firstInteraction: number | null = null
  private lastInteraction: number
  private selections = 0
  private copies = 0
  private hovers = 0
  private clicks = 0

  private stopped = false
  private lastSection: number | null = null
  private exitReason: ExitReason | null = null
  private ended = false
  private engagedSent = false

  constructor(init: ViewInit) {
    this.viewId = init.viewId
    this.sessionId = init.sessionId
    this.turnKey = init.turnKey
    this.requestId = init.requestId ?? null
    this.mode = init.mode ?? null
    this.streamed = init.streamed
    this.streamStart = init.streamed ? init.now : null
    this.completedAt = init.streamed ? null : init.now
    this.last = init.now
    this.lastInteraction = init.now
    this.active = init.active ?? true
    this.visible = init.visible ?? true
  }

  get isEnded(): boolean { return this.ended }
  get isStreaming(): boolean { return this.streamed && this.completedAt === null }

  /** Advance the clocks to `t`, crediting the time since the last event to whatever was true during it. */
  private tick(t: number): void {
    const dt = Math.max(0, t - this.last)
    this.last = Math.max(this.last, t)
    if (!dt) return
    if (!this.visible) this.hidden += dt
    if (this.active && !this.ended) {
      this.dwell += dt
      for (const i of this.onScreen) this.ms[i] = (this.ms[i] ?? 0) + dt
    }
  }

  /** The answer's blocks in order, with their word counts (from textContent, counted locally). */
  setSections(words: number[], t: number): void {
    this.tick(t)
    this.words = words.slice(0, MAX_SECTIONS).map(int)
    for (const i of [...this.onScreen]) if (i >= this.words.length) this.onScreen.delete(i)
  }

  setSourcesCount(n: number): void { this.sourcesCount = int(n) }

  /** The blocks currently on screen (half visible or more). A block entering view while the reader is active is a visit. */
  setOnScreen(indexes: Iterable<number>, t: number): void {
    this.tick(t)
    const next = new Set([...indexes].filter((i) => i >= 0 && i < MAX_SECTIONS))
    for (const i of next) {
      if (!this.onScreen.has(i)) this.visits[i] = (this.visits[i] ?? 0) + 1
    }
    this.onScreen = next
    if (next.size) this.lastSection = Math.max(...next)
  }

  /** Page visible AND window focused. */
  setActive(active: boolean, t: number): void {
    this.tick(t)
    this.active = active
  }

  /** Tab visibility, for hidden_ms and tab_switches. */
  setVisible(visible: boolean, t: number): void {
    this.tick(t)
    if (this.visible && !visible) this.tabSwitches++
    this.visible = visible
  }

  streamComplete(t: number): void {
    this.tick(t)
    if (!this.streamed || this.completedAt !== null) return
    this.completedAt = t
    this.streamMs = int(t - (this.streamStart ?? t))
    this.lastInteraction = t
  }

  /** The reader pressed stop. */
  stop(t: number): void {
    this.tick(t)
    if (this.isStreaming) this.stopped = true
  }

  /** Deepest part of the answer seen, 0-100. */
  setDepth(pct: number, t: number): void {
    this.tick(t)
    this.depth = Math.max(this.depth, Math.min(100, Math.max(0, Math.round(pct))))
  }

  /** Scroll position in px. A reversal is a downward run followed by an upward run of at least 120px. */
  scroll(y: number, t: number): void {
    this.tick(t)
    this.interact(t)
    if (this.peak === null || this.trough === null) {
      this.peak = y
      this.trough = y
      return
    }
    if (!this.rising) {
      // Going down (or still): follow the peak; a climb back of 120px from it is a reversal.
      if (y > this.peak) this.peak = y
      else if (this.peak - y >= REVERSAL_PX) {
        this.reversals++
        this.rising = true
        this.trough = y
      }
    } else {
      if (y < this.trough) this.trough = y
      else if (y - this.trough >= REVERSAL_PX) {
        this.rising = false
        this.peak = y
      }
    }
  }

  /** Any click, key, selection or scroll. The first one is timed from the answer being complete (or open). */
  interact(t: number): void {
    this.lastInteraction = t
    if (this.firstInteraction === null && this.completedAt !== null) this.firstInteraction = int(t - this.completedAt)
  }

  selection(t: number): void { this.tick(t); this.interact(t); this.selections++ }
  copy(t: number): void { this.tick(t); this.interact(t); this.copies++ }
  citationHover(durationMs: number): void { if (durationMs >= HOVER_MS) this.hovers++ }
  citationClick(t: number): void { this.tick(t); this.interact(t); this.clicks++ }

  /** True once, the moment the view crosses coverage >= 0.5 and dwell >= 10s (the funnel step). */
  engagedNow(t: number): boolean {
    if (this.engagedSent) return false
    const s = this.snapshot(t)
    if (s.dwell_ms < ENGAGED_DWELL_MS) return false
    if (coverage(s.sections, s.answer_words) < ENGAGED_COVERAGE) return false
    this.engagedSent = true
    return true
  }

  /** End the view: the reason, and (for the payload) whether it was aborted. */
  end(reason: ExitReason, t: number): void {
    if (this.ended) return
    this.tick(t)
    // A reader who stopped interacting two minutes ago and then left went idle, whichever way they left.
    const idle = t - this.lastInteraction >= IDLE_MS
    this.exitReason = reason !== "followup" && idle ? "idle" : reason
    this.ended = true
    this.endedAt = t
  }
  private endedAt: number | null = null

  snapshot(t: number, final = false): EngagementPayload {
    this.tick(t)
    const sections = this.words.map((w, i) => ({ i, words: w, visible_ms: int(this.ms[i] ?? 0), visits: int(this.visits[i] ?? 0) }))
    const answerWords = this.words.reduce((a, b) => a + b, 0)
    const leftAt = this.endedAt ?? t
    const leftStreaming = this.ended && this.isStreaming
    let abortPhase: AbortPhase | null = null
    if (this.stopped || leftStreaming) abortPhase = "streaming"
    else if (this.ended && this.completedAt !== null && leftAt - this.completedAt < READING_ABORT_MS && this.depth < READING_ABORT_DEPTH) abortPhase = "reading"
    return {
      app: "nira",
      view_id: this.viewId,
      session_id: this.sessionId,
      turn_key: this.turnKey.slice(0, 100),
      request_id: this.requestId,
      mode: this.mode,
      answer_words: answerWords,
      section_count: sections.length,
      sources_count: this.sourcesCount,
      streamed: this.streamed,
      stream_ms: this.streamed ? this.streamMs : null,
      first_interaction_ms: this.firstInteraction,
      dwell_ms: int(this.dwell),
      hidden_ms: int(this.hidden),
      tab_switches: this.tabSwitches,
      max_depth_pct: this.depth,
      scroll_reversals: this.reversals,
      selections: this.selections,
      copies: this.copies,
      citation_hovers: this.hovers,
      citation_clicks: this.clicks,
      sections,
      aborted: abortPhase !== null,
      abort_phase: abortPhase,
      abort_section: abortPhase !== null ? this.lastSection : null,
      exit: this.exitReason,
      final: final || this.ended,
    }
  }
}

/** The numbers PostHog gets for a view (`answer_view_summary`), from the same payload the server stores. */
export function summaryOf(p: EngagementPayload) {
  const effort = readingEffort(p.sections as SectionStat[], p.dwell_ms, p.answer_words)
  const reread = rereadRatio(p.sections)
  return {
    effort,
    reread,
    coverage: coverage(p.sections, p.answer_words),
    loadScoreBase: loadScoreBase(effort, reread, p.scroll_reversals, p.selections),
  }
}
